/* PDP — Before / after results.
   Paired with sections/product-before-after.liquid.

   Two jobs:
   1. The tabs switch the visible photo pair, as a tablist (arrow keys included).
   2. A variant change selects the tab whose option value the new variant
      carries, so the photos always show the shade being bought. The theme
      dispatches `product:variant-change` on `document` with the variant in
      `event.detail` — see assets/theme.js and assets/custom-events.js.

   Written in ES5 with no dependencies, matching assets/quantity-breaks.js: this
   loads on every PDP and must not need a build step or a polyfill. */
(function () {
  'use strict';

  /* The section ships its own <script> tag, so a page carrying two of these
     loads - and runs - this file twice, binding the document listeners below
     twice with it. initAll() already walks every widget on the page, so one
     execution serves them all. Same guard as assets/quantity-breaks.js. */
  if (window.productBeforeAfterInitialized) return;
  window.productBeforeAfterInitialized = true;

  function find(root, selector) {
    return root.querySelector(selector);
  }

  function findAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /* Only the tabs and panels belonging to this widget, never a nested one. A
     PDP can carry two of these sections (say, mascara and liner). */
  function ownTabs(widget) {
    return findAll(widget, '[data-pba-tab]').filter(function (tab) {
      return tab.closest('[data-pba]') === widget;
    });
  }

  function ownPanels(widget) {
    return findAll(widget, '[data-pba-panel]').filter(function (panel) {
      return panel.closest('[data-pba]') === widget;
    });
  }

  function select(widget, index, options) {
    var settings = options || {};
    var tabs = ownTabs(widget);
    var panels = ownPanels(widget);
    if (index < 0 || index >= tabs.length) return;

    tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      /* Roving tabindex: one stop for the whole tablist, arrow keys move
         within it. */
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    panels.forEach(function (panel, i) {
      var active = i === index;
      panel.classList.toggle('is-active', active);
      if (active) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (settings.focus && tabs[index]) tabs[index].focus();
    if (settings.syncVariant) syncVariant(widget, tabs[index]);
  }

  function activeIndex(widget) {
    var tabs = ownTabs(widget);
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].classList.contains('is-active')) return i;
    }
    return 0;
  }

  /* Reverse binding, off unless the section turns it on: clicking a tab picks
     the matching option in the theme's own variant picker. We click the
     picker's control rather than setting a value, so the theme's handler runs
     and stays the single source of truth for the selected variant — which then
     dispatches `product:variant-change` straight back here. That round trip is
     why `select` is idempotent. */
  function syncVariant(widget, tab) {
    if (!tab) return;
    if (widget.getAttribute('data-pba-sync-variant') !== 'true') return;

    var wanted = normalise(tab.getAttribute('data-pba-option-value'));
    if (!wanted) return;

    var form = document.querySelector('[data-product-form], form[action*="/cart/add"]');
    if (!form) return;

    /* Swatches and chips are buttons carrying `data-option-value`; the plain
       dropdown fallback is a select of the same values. */
    var buttons = findAll(form, '[data-option-buttons] [data-option-value]');
    for (var i = 0; i < buttons.length; i++) {
      if (normalise(buttons[i].getAttribute('data-option-value')) === wanted) {
        if (!buttons[i].classList.contains('selected')) buttons[i].click();
        return;
      }
    }

    var selects = findAll(form, 'select[name^="options"]');
    for (var j = 0; j < selects.length; j++) {
      var opts = findAll(selects[j], 'option');
      for (var k = 0; k < opts.length; k++) {
        if (normalise(opts[k].value) === wanted) {
          if (selects[j].value === opts[k].value) return;
          selects[j].value = opts[k].value;
          selects[j].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  /* A variant carries its option values in `options`; older payloads only have
     the joined `title` ("Black / 10ml"), so both are searched. */
  function variantValues(variant) {
    var values = [];
    if (variant.options && variant.options.length) {
      values = variant.options.slice();
    } else if (variant.title) {
      values = String(variant.title).split(' / ');
    }
    return values.map(normalise);
  }

  function selectForVariant(widget, variant) {
    if (!variant) return;
    var values = variantValues(variant);
    if (!values.length) return;

    var tabs = ownTabs(widget);
    for (var i = 0; i < tabs.length; i++) {
      var option = normalise(tabs[i].getAttribute('data-pba-option-value'));
      if (!option) continue;
      if (values.indexOf(option) !== -1) {
        /* Never sync back: this call came *from* a variant change, and driving
           the picker from here would be a loop. */
        if (i !== activeIndex(widget)) select(widget, i);
        return;
      }
    }
  }

  var KEYS = { end: 35, home: 36, left: 37, right: 39 };

  function init(widget) {
    if (widget.hasAttribute('data-pba-ready')) return;
    widget.setAttribute('data-pba-ready', 'true');

    widget.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-pba-tab]');
      if (!tab || tab.closest('[data-pba]') !== widget) return;
      event.preventDefault();
      select(widget, ownTabs(widget).indexOf(tab), { syncVariant: true });
    });

    /* Add to cart, using whatever variant is selected in the main product form
       above. Read at click time so it always matches the current selection —
       including a shade the shopper picked through this section's own tabs
       when tab-to-variant sync is on. Mirrors assets/quantity-breaks.js so the
       cart drawer opens the same way it does everywhere else on the PDP. */
    widget.addEventListener('click', function (event) {
      var button = event.target.closest('[data-pba-atc]');
      if (!button || button.closest('[data-pba]') !== widget) return;
      event.preventDefault();
      addToCart(widget, button);
    });

    widget.addEventListener('keydown', function (event) {
      var tab = event.target.closest('[data-pba-tab]');
      if (!tab || tab.closest('[data-pba]') !== widget) return;

      var tabs = ownTabs(widget);
      var current = tabs.indexOf(tab);
      var next = -1;

      if (event.keyCode === KEYS.left) next = current === 0 ? tabs.length - 1 : current - 1;
      else if (event.keyCode === KEYS.right) next = current === tabs.length - 1 ? 0 : current + 1;
      else if (event.keyCode === KEYS.home) next = 0;
      else if (event.keyCode === KEYS.end) next = tabs.length - 1;
      else return;

      event.preventDefault();
      select(widget, next, { focus: true, syncVariant: true });
    });

    selectForCurrentSelection(widget);
  }

  /* The picker may already be on a deep-linked ?variant= shade by the time this
     runs, and the theme only dispatches `product:variant-change` on a *change*.
     Read the picker's current state instead of waiting for an event that has
     already been and gone, so the card opens on the shade being bought. */
  function selectForCurrentSelection(widget) {
    var form = document.querySelector('[data-product-form], form[action*="/cart/add"]');
    if (!form) return;

    var values = findAll(form, '[data-option-buttons] [data-option-value].selected')
      .map(function (button) { return normalise(button.getAttribute('data-option-value')); });

    findAll(form, 'select[name^="options"]').forEach(function (select_) {
      values.push(normalise(select_.value));
    });

    if (!values.length) return;

    var tabs = ownTabs(widget);
    for (var i = 0; i < tabs.length; i++) {
      var option = normalise(tabs[i].getAttribute('data-pba-option-value'));
      if (option && values.indexOf(option) !== -1) {
        if (i !== activeIndex(widget)) select(widget, i);
        return;
      }
    }
  }

  function setError(widget, message) {
    var slot = find(widget, '[data-pba-error]');
    if (slot) slot.textContent = message || '';
  }

  /* Merchant-facing copy is a section setting, matching the quantity breaks
     block - so both strings stay translatable through Translate & Adapt
     instead of being English hard-coded in an asset. The literals are the
     last-resort fallback for a section saved before the settings existed. */
  function errorText(widget, name, fallback) {
    return widget.getAttribute('data-pba-' + name) || fallback;
  }

  function addToCart(widget, button) {
    var form = document.querySelector('[data-product-form], form[action*="/cart/add"]');
    var idInput = form && find(form, '[name="id"]');
    var variantId = idInput && idInput.value;

    if (!variantId) {
      setError(widget, errorText(widget, 'choose-error', 'Choose an option above first.'));
      return;
    }

    /* Built explicitly rather than serialising the form, so a bundle app's own
       hidden inputs cannot ride along — the same reasoning as quantity-breaks.js.
       Line-item properties are kept, because they are the shopper's input. */
    var body = 'id=' + encodeURIComponent(variantId) + '&quantity=1';
    findAll(form, '[name^="properties"]').forEach(function (input) {
      if (input.type === 'checkbox' && !input.checked) return;
      body += '&' + encodeURIComponent(input.name) + '=' + encodeURIComponent(input.value);
    });

    setError(widget, '');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    var idle = button.getAttribute('data-label') || button.textContent;
    var busy = button.getAttribute('data-busy-label');
    if (busy) button.textContent = busy;

    var restore = function (text) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = text;
    };

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (response) {
      return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    }).then(function (result) {
      if (!result.ok) {
        /* Shopify's own description is already in the shopper's language. */
        restore(idle);
        setError(widget, (result.data && result.data.description) || errorText(widget, 'add-error', 'Sorry, that could not be added.'));
        return;
      }

      var done = button.getAttribute('data-done-label');
      restore(done || idle);
      if (done) window.setTimeout(function () { button.textContent = idle; }, 2000);

      /* The theme refreshes its cart count off this event, and the header icon
         is what actually opens its drawer. */
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      var icon = find(document, '[data-js-cart-icon]');
      if (icon) icon.click();
    })['catch'](function () {
      restore(idle);
      setError(widget, errorText(widget, 'add-error', 'Sorry, that could not be added.'));
    });
  }

  function initAll(root) {
    findAll(root || document, '[data-pba]').forEach(init);
  }

  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    findAll(document, '[data-pba]').forEach(function (widget) {
      selectForVariant(widget, variant);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('quickview:loaded', function () { initAll(); });
})();
